import { axiosPrivate } from "@/api/axios";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import MediaFormField from "@/components/ui/mediaFormField";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import RichTextEditor from "@/components/ui/RichTextEditor";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { z } from "zod";
import { uploadFileToFirebase } from "@/lib/firebase/mediaUploading";

const postFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  content: z.string().min(1, "Content is required"),
  type: z.enum(["reel", "video", "post"], {
    errorMap: () => ({ message: "Type is required" }),
  }),
  category_id: z.coerce.number().min(1, "Category is required"),
  subcategory: z.string().optional(),
  tags: z.string().optional(),
  author: z.string().optional(),
  source: z.string().optional(),
  is_featured: z.boolean().optional(),
});

function AddPost() {
  const [media, setMedia] = React.useState<File | null>(null),
    [mediaUrl, setMediaUrl] = React.useState(""),
    [thumbnailUrl, setThumbnailUrl] = React.useState("");
  const [categories, setCategories] = React.useState<{ id: number; name: string }[]>([]);
  const [loadingCategories, setLoadingCategories] = React.useState(true);

  React.useEffect(() => {
  async function fetchCategories() {
      try {
    const res = await axiosPrivate.get("/feeds/categories");
        setCategories(res.data.data || []);
      } catch (err) {
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    }
    fetchCategories();
  }, []);
  const form = useForm({
    resolver: zodResolver(postFormSchema),
    defaultValues: {
      title: "",
      description: "",
      content: "",
      type: "post" as "reel" | "video" | "post",
      category_id: 0,
      subcategory: "",
      tags: "",
      author: "",
      source: "",
      is_featured: false,
    },
  });
  const navigate = useNavigate();

  const postCreateMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axiosPrivate.post("/feeds", data);
      return response.data.data;
    },
    onSuccess: () => {
      form.reset();
      setMedia(null);
      setMediaUrl("");
      toast.success("Post created successfully");
      navigate("/dashboard/feed/manage-post");
    },
    onError: () => {
      toast.error("Failed to create post");
    },
  });

  async function onSubmit(data: z.infer<typeof postFormSchema>) {
    let finalMediaUrl = mediaUrl;
    let finalThumbnailUrl = thumbnailUrl;
    
    if (media) {
      try {
        finalMediaUrl = await uploadFileToFirebase(media, "posts_media");
        setMediaUrl(finalMediaUrl);
        // For videos, we might want to generate a thumbnail or use the same URL
        if (data.type === "video" && !finalThumbnailUrl) {
          finalThumbnailUrl = finalMediaUrl;
        }
      } catch (err) {
        toast.error("Failed to upload media to Firebase");
        return;
      }
    }
    
    // Structure the payload to match the API schema
    // Ensure there's a clear new line before description for the mobile app display
    const descriptionWithNewline = data.description.startsWith("\n")
      ? data.description
      : `\n${data.description}`;

    const payload = {
      title: data.title,
      description: descriptionWithNewline,
      content: data.content,
      media_url: finalMediaUrl,
      thumbnail_url: finalThumbnailUrl || undefined,
      type: data.type,
      category_id: data.category_id,
      tags: data.tags || undefined,
      author: data.author || undefined,
      source: data.source || undefined,
      is_featured: data.is_featured || false,
    };
    
    // Remove undefined values to match API expectations
    Object.keys(payload).forEach(key => {
      if (payload[key as keyof typeof payload] === undefined) {
        delete payload[key as keyof typeof payload];
      }
    });
    
    postCreateMutation.mutate(payload);
  }

  return (
    <div className="space-y-4 sm:space-y-8">
      <h1 className="text-2xl">Add Post</h1>
      <Form {...form}>
        <form className="space-y-4 sm:space-y-8" onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              name="type"
              control={form.control}
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel className="after:content-['*'] after:text-red-500 after:ml-1">Type of Post</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type of post" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="reel">Reel</SelectItem>
                        <SelectItem value="video">Video</SelectItem>
                        <SelectItem value="post">Post</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="category_id"
              control={form.control}
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel className="after:content-['*'] after:text-red-500 after:ml-1">Category</FormLabel>
                  <FormControl>
                    <Select
                      value={field.value && field.value > 0 ? String(field.value) : ""}
                      onValueChange={val => field.onChange(Number(val))}
                      disabled={loadingCategories}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={loadingCategories ? "Loading..." : "Select Category"} />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.length === 0 && !loadingCategories && (
                          <SelectItem value="no-categories" disabled>No categories available</SelectItem>
                        )}
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={String(cat.id)}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            // />
            // <FormField
            //   name="subcategory"
            //   control={form.control}
            //   render={({ field }) => (
            //     <FormItem className="space-y-1">
            //       <FormLabel>Sub Category</FormLabel>
            //       <FormControl>
            //         <Select value={field?.value || ""} onValueChange={field.onChange}>
            //           <SelectTrigger>
            //             <SelectValue placeholder="Select Sub Category" />
            //           </SelectTrigger>
            //           <SelectContent>
            //             <SelectItem value="subcategory1">Sub Category 1</SelectItem>
            //             <SelectItem value="subcategory2">Sub Category 2</SelectItem>
            //             <SelectItem value="subcategory3">Sub Category 3</SelectItem>
            //           </SelectContent>
            //         </Select>
            //       </FormControl>
            //       <FormMessage />
            //     </FormItem>
            //   )}
            />
          </div>
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel className="after:content-['*'] after:text-red-500 after:ml-1">Title</FormLabel>
                <FormControl>
                  <Input placeholder="Enter Post Title" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel className="after:content-['*'] after:text-red-500 after:ml-1">Description</FormLabel>
                <FormControl>
                  <RichTextEditor
                    value={field.value || ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder="Enter Post Description"
                    minHeight="320px"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="content"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel className="after:content-['*'] after:text-red-500 after:ml-1">Content</FormLabel>
                <FormControl>
                  <RichTextEditor
                    value={field.value || ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder="Enter Post Content"
                    minHeight="320px"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {/* <FormField
            control={form.control}
            name="link"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel>Link</FormLabel>
                <FormControl>
                  <Input placeholder="Enter Notification Link" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          /> */}
          {/* <div>
            <p>Filters</p>
            <div className="flex items-center gap-10">
              <FormField
                control={form.control}
                name="age"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Age</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Age" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Array.from({ length: 83 }, (_, i) => i + 18).map((i) => (
                          <SelectItem key={i} value={i.toString()}>{i}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="gender"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Gender</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Gender" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={"male"}>Male</SelectItem>
                        <SelectItem value={"female"}>Female</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div> */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              name="author"
              control={form.control}
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Author (optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter author name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            name="source"
            control={form.control}
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel>Source (optional)</FormLabel>
                <FormControl>
                  <Textarea rows={3} placeholder="Enter source" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="space-y-3">
            <Label>Image / Video</Label>
            <MediaFormField
              media={media}
              setMedia={setMedia}
              setMediaUrl={setMediaUrl}
              refPath="posts_media"
            />
          </div>
          <FormField
            name="tags"
            control={form.control}
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel>Tags (comma separated)</FormLabel>
                <FormControl>
                  <Input placeholder="Enter tags" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="is_featured"
            control={form.control}
            render={({ field }) => (
              <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                <FormControl>
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={e => field.onChange(e.target.checked)}
                  />
                </FormControl>
                <FormLabel className="mb-0">Featured</FormLabel>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            disabled={
              postCreateMutation.isPending ||
              !form.formState.isValid ||
              postCreateMutation.isSuccess
            }
          >
            Create Feed
          </Button>
        </form>
      </Form>
    </div>
  );
}

export default AddPost;
